import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Stack} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import {Box, Grid} from "@mui/material";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import CurrencyYuanOutlinedIcon from '@mui/icons-material/CurrencyYuanOutlined';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useCreateExpense} from "@/service/expense.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, getSelectValues, setValues} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {TagOut} from "@/widget/TagOut/TagOut";
import IconButton from "@mui/material/IconButton";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import {TitleBar} from "@/widget/TitleBar/TitleBar";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";

const validationFundSchema = z.object({
    name: z.string({message: "请填写款项名称"}),
    money: z.number({message: '请填款项金额'}).max(1000000, "金额不能大于百万"),
    remark: z.unknown()
})

const validationSchema = z.object({
    customerName: z.unknown(),
    customerId: z.unknown(),
    totalMoney: z.number({message: '请填写总金额'}).max(1000000, "金额不能大于百万"),
    payTime: z.date({message: "请选择付款时间"}),
    remark: z.ostring(),
    receiptName: z.string({message: "请填写收款名称"}),
    receiptBank: z.string({message: "请填写收款银行"}),
    receiptAccount: z.string({message: "请填写收款账号"}),
    funds: z.array(validationFundSchema),
    categoryIds: z.string({message: "请选择支付类型"}),
    tempCategories: z.array(z.object({label: z.string(), value: z.number()})),
    categories: z.string().optional(),
});

export type WriteExpenseForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteExpense = () => {
    const {expenseType} = useAllDict()
    const {setToast} = useGlobalActions()
    const {mutateAsync: createExpenseMutate, isPending: createExpensePending} = useCreateExpense()
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()
    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteExpenseForm>({
        resolver: zodResolver(validationSchema),
        defaultValues: {...defaultValues, funds: [{}]},
    });

    const onSubmit = async (values: WriteExpenseForm) => {
        const result = await createExpenseMutate(values)
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const {fields, append, remove} = useFieldArray({
        name: "funds",
        control,
        keyName: "_id"
    });

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>支付类型</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                value={value || []}
                                slotProps={{chip: {size: 'small',}}}
                                options={expenseType || []}
                                disableListWrap={true}
                                renderTags={(values) => {
                                    return values?.map((value: DictPO, index: number) => <TagOut key={index} index={index} label={value.label}/>)
                                }}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "label", val: 'value'}, {key: "categoryIds", val: "categories"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.categoryIds}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.categoryIds?.message} placeholder={"选择支付类型"} {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempCategories"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="paymentDate">支付时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择支付时间"}
                                customInput={<TextField
                                    error={!!errors?.payTime}
                                    helperText={errors?.payTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"payTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="totalMoney">总金额</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.totalMoney}
                            helperText={errors?.totalMoney?.message}
                            id="totalMoney"
                            type={'number'}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value))}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000000,
                                        maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <CurrencyYuanOutlinedIcon color="disabled"/>,
                                },
                            }}
                            placeholder="填写总金额"
                            variant="outlined"
                        />} name={'totalMoney'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteExpenseForm, CustomerPO> control={control} attr={"customerName"}
                                                              freeSolo={true} attrId={"customerId"}
                                                              errMsg={errors.customerId?.message}
                                                              setValue={setValue}
                                                              label={"关联客户"}/>
                </Grid>
                <TitleBar title={"支出项"} extra={<IconButton
                    size={"small"}
                    onClick={() => append({name: "", money: 0})}
                    color={'default'}
                    sx={{padding: 0}}>
                    <ControlPointOutlinedIcon/>
                </IconButton>}/>
                {fields.map((field, index) => {
                    return (
                        <Stack
                            key={index}
                            direction={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            alignContent={"center"}
                            width={'100%'}
                        >
                            <Grid spacing={2} container>
                                <Grid size={6}>
                                    <FormControl fullWidth={true} required={true}>
                                        <FormLabel>款项名称</FormLabel>
                                        <TextField
                                            error={!!errors?.funds?.[index]?.name}
                                            helperText={errors?.funds?.[index]?.name?.message}
                                            {...register(`funds.${index}.name`)}
                                            slotProps={{
                                                input: {
                                                    inputProps: {maxLength: 10},
                                                    endAdornment: (
                                                        <PermIdentityOutlinedIcon color="disabled"/>
                                                    ),
                                                },
                                            }}
                                            placeholder="填写款项名称"
                                            variant="outlined"
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid size={6}>
                                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                                        <FormLabel htmlFor="totalMoney">款项金额</FormLabel>
                                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                            error={!!errors?.funds?.[index]?.name}
                                            helperText={errors?.funds?.[index]?.name?.message}
                                            type={'number'}
                                            value={value}
                                            onChange={event => onChange(parseInt(event.target.value) || null)}
                                            slotProps={{
                                                input: {
                                                    inputProps: {
                                                        min: 0,
                                                        max: 1000000,
                                                        maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                                    },
                                                    endAdornment: <CurrencyYuanOutlinedIcon color="disabled"/>,
                                                },
                                            }}
                                            placeholder="填写款项金额"
                                            variant="outlined"
                                        />} name={`funds.${index}.money`}/>
                                    </FormControl>

                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth={true}>
                                        <FormLabel>摘要</FormLabel>
                                        <TextField
                                            rows={3}
                                            multiline
                                            {...register(`funds.${index}.remark`)}
                                            slotProps={{
                                                input: {
                                                    sx: {height: 'auto'},
                                                    inputProps: {maxLength: 250},
                                                },
                                            }}
                                            placeholder="填写摘要"
                                            fullWidth
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {fields?.length > 1 ? (
                                <IconButton
                                    onClick={() => remove(index)}
                                    sx={{padding: 0}}
                                    size={"small"}
                                >
                                    <RemoveCircleOutlineOutlinedIcon color={"error"}/>
                                </IconButton>
                            ) : <></>}
                        </Stack>)
                })}
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>收款名称</FormLabel>
                        <TextField
                            {...register("receiptName")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写收款名称"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>收款账号</FormLabel>
                        <TextField
                            {...register("receiptAccount")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                },
                            }}
                            placeholder="填写收款账号"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>收款银行</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("receiptBank")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="输入收款银行"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createExpensePending} onReset={() => reset(defaultValues)}/>
        </Box>
    )
        ;
};
